import { CurrentUserStore } from '@frontendlib/common/services/current-user.store';
import { Component, computed, inject, Injector, OnDestroy, OnInit, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MessagesService } from '@modules/messages/services/messages.service';
import { type Message } from '@modules/messages/types/message';
import { type BaseStitchService } from '@modules/common/services/base-stitch.service';
import { MessagesFilters } from '@modules/messages/types/messages-filters';
import { CalendarsListStateService } from '@modules/calendar-app/services/calendars-list-state.service';
import { CalendarsService } from '@modules/calendar-app/services/calendars.service';
import { EventsListStateService } from '@modules/calendar-app/services/events-list-state.service';
import { EventsService } from '@modules/calendar-app/services/events.service';
import { CalendarAppStateService } from '@modules/calendar-app/services/state.service';
import { Calendar } from '@modules/calendar-app/types/calendar';
import { CalendarEvent } from '@modules/calendar-app/types/calendar-event';
import { CalendarsFilters } from '@modules/calendar-app/types/calendars-filters';
import { EventsFilters } from '@modules/calendar-app/types/events-filters';
import { ListState } from '@modules/calendar-app/types/list-state';
import { VirtualFolder } from '@modules/calendar-app/types/virtual-folder';
import { Application } from '@modules/common/types/application';
import { ListMode } from '@modules/common/types/ListMode';
import { isKnownTab, Tab } from '@modules/common/types/tab';
import { SelectableListChain } from '@modules/drag-n-drop/types/selectable-list-chain';
import { DropdownOption } from '@modules/dropdown/types/dropdown-option';
import { SortOrder } from '@modules/common/types/sort-order';
import { ModalService } from '@modules/modal/services/modal.service';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { SplitViewService } from '@modules/split-view/services/split-view.service';
import { MessageDirection } from '@sharedlib/common/types/mail-app/MessageDirection';
import { MessageState } from '@sharedlib/common/types/mail-app/MessageState';
import type { StitchType } from '@sharedlib/common/types/StitchType';
import type { CalendarEventSummaryDto } from '@sharedlib/dtos/dto/calendar-app/event.dto';
import type { CalendarSummaryDto } from '@sharedlib/dtos/dto/calendar-app/calendar.dto';
import type { MessageSummaryDto } from '@sharedlib/dtos/dto/mail-app/message.dto';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';

@Component({
  selector: 'app-events-list-container',
  templateUrl: './events-list-container.component.html',
  styleUrls: ['./events-list-container.component.less'],
  standalone: false,
})
export class EventsListContainerComponent implements OnInit, OnDestroy {
  public selectedOrder: DropdownOption;
  public listState: ListState;
  public listScrollPosition: number;
  public minimized: boolean;
  public loading: boolean;

  public calendarsFilters: CalendarsFilters;
  public debug: 'score' = null;
  public quickEvent: CalendarEvent;
  public quickCalendar = new Calendar();
  public selectedTab: Exclude<ListState, 'tabs'> = 'events';
  public tabs: Tab<Exclude<ListState, 'tabs'>>[] = [
    { title: 'Calendars', value: 'calendars' },
    { title: 'Events', value: 'events' },
  ];
  protected readonly ListMode = ListMode;
  /** The sticky list and the date-range list below it hold one selection and share the arrow keys. */
  protected readonly selectionChain = new SelectableListChain<CalendarEventSummaryDto>();
  private readonly alive = new Subject<void>();
  private readonly searchTitle = new BehaviorSubject('');

  public readonly eventsListStateService = inject(EventsListStateService);
  private readonly calendarsListStateService = inject(CalendarsListStateService);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);

  /**
   * The account zone as a SOURCE, not a one-off read (SBUX-829) — see EventsFilters.applyListState.
   */
  private readonly displayTimeZone$ = this.displayTimeZoneService.resolvedTimeZone$;
  private readonly advancedSearchService = inject(AdvancedSearchService);
  private readonly calendarAppStateService = inject(CalendarAppStateService);
  private readonly currentUser = inject(CurrentUserStore);
  private readonly selectedTenant = toSignal(this.calendarAppStateService.getTeam());
  protected readonly invitationTenantItems = computed(() => {
    const tenant = this.selectedTenant();
    if (tenant === undefined) return null;
    const accountId = tenant ?? this.currentUser.accountId();
    return accountId ? [{ accountId }] : null;
  });

  private readonly eventsService = inject(EventsService);
  private readonly calendarsService = inject(CalendarsService);
  // Mail delegates to the base reader, whose model argument preserves the message's owner.
  private readonly messagesService: BaseStitchService<StitchType.MESSAGE, MessageSummaryDto, Message, MessagesFilters> =
    inject(MessagesService);

  private readonly splitViewService = inject(SplitViewService);
  private readonly modalService = inject(ModalService);
  private readonly injector = inject(Injector);

  protected readonly isAdvancedSearchApplied: Signal<boolean> = toSignal(
    this.advancedSearchService.isAdvancedSearchApplied(Application.calendar),
    {
      initialValue: false,
    },
  );
  protected readonly virtualFolder: Signal<VirtualFolder> = toSignal(this.calendarAppStateService.getVirtualFolder());
  private readonly virtualFolderReselections: Signal<number> = toSignal(
    this.calendarAppStateService.getVirtualFolderReselections(),
    { initialValue: 0 },
  );
  protected readonly containerName: Signal<string | null> = toSignal(
    this.advancedSearchService.getState().pipe(map((state) => state[Application.calendar]?.containerName ?? null)),
    { initialValue: null },
  );
  protected readonly eventsFilters: Signal<EventsFilters> = toSignal(
    combineLatest([
      this.eventsListStateService.value(),
      this.advancedSearchService.getState(),
      this.calendarAppStateService.getVirtualFolder(),
      this.calendarAppStateService.getSelectedCalendarUids(),
      this.displayTimeZone$,
      this.calendarAppStateService.getTeam(),
      this.calendarAppStateService.getVirtualFolderReselections(),
    ]).pipe(
      map(([list, search, folder, selectedCalendarUids, displayTimeZone]) => {
        return new EventsFilters({
          containersIds: selectedCalendarUids,
          withEmpty: selectedCalendarUids.length === 0,
          displayTimeZone,
        })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder);
      }),
      takeUntil(this.alive),
    ),
  );
  /** Which invites folder is selected, if any — these list mail messages instead of events (SBUX-911). */
  protected readonly invitesFolder: Signal<'received' | 'sent' | null> = computed(() => {
    switch (this.virtualFolder()) {
      case 'received_invites':
        return 'received';
      case 'sent_invites':
        return 'sent';
      default:
        return null;
    }
  });

  /**
   * Mailbox filters backing the invites folders: calendar invite notifications only, shaped like
   * Inbox (received) / Sent (sent). Archiving or trashing the email removes it from the folder (R-2.2).
   */
  protected readonly messagesFilters: Signal<MessagesFilters | null> = computed(() => {
    this.virtualFolderReselections(); // a re-click rebuilds the filters, which resets the list
    const invites = this.invitesFolder();

    if (!invites) {
      return null;
    }

    return new MessagesFilters({
      eventInvite: true,
      direction: invites === 'received' ? MessageDirection.INCOMING : MessageDirection.OUTGOING,
      state: invites === 'received' ? MessageState.RECEIVED : MessageState.SENT,
      archived: false,
      deleted: false,
      spam: false,
      // Mail lists read newest-first; the base Filters default is 'asc'.
      sortOrder: SortOrder.DESC,
    });
  });

  protected readonly eventsListMode: Signal<ListMode> = computed(() => {
    if (this.isAdvancedSearchApplied()) {
      return ListMode.LIST;
    }

    if (this.virtualFolder() === 'all_events') {
      return ListMode.DATE_RANGE;
    }
    return ListMode.LIST;
  });

  public ngOnInit() {
    combineLatest([
      this.calendarsListStateService.value(),
      this.advancedSearchService.getState(),
      this.calendarAppStateService.getVirtualFolder(),
      this.displayTimeZone$,
      this.calendarAppStateService.getTeam(),
      this.calendarAppStateService.getVirtualFolderReselections(),
    ])
      .pipe(filter(([, , , , tenant]) => tenant !== undefined), takeUntil(this.alive))
      .subscribe(([list, search, folder, displayTimeZone]) => {
        // `displayTimeZone` FIRST: applyListState reads it to reckon its day windows in the account
        // zone (SBUX-829).
        this.calendarsFilters = new CalendarsFilters({ displayTimeZone })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder);
      });

    this.splitViewService
      .getMinimized('eventsList')
      .pipe(takeUntil(this.alive))
      .subscribe((minimized: boolean) => {
        this.minimized = minimized;
      });

    combineLatest([this.advancedSearchService.getState(), this.calendarAppStateService.getVirtualFolder()])
      .pipe(takeUntil(this.alive))
      .subscribe(([state, folder]) => {
        this.quickEvent = CalendarEvent.fromAdvancedState(state, folder);
      });

    this.calendarAppStateService
      .getTabs()
      .pipe(takeUntil(this.alive))
      .subscribe((state: ListState) => {
        this.listState = state;
        if (this.listState !== 'tabs') {
          this.selectedTab = this.listState;
        }
        // Unconditional, not just on the non-'tabs' branch above: a container recreated by
        // stchSpaceRerender (e.g. on a space switch) re-inits `selectedTab` to its field default, but
        // the app-state service instance survives the switch and keeps whatever tab was last published
        // — republishing here on every emission, including while folder-mapped to 'tabs', overwrites
        // that stale value as soon as this container exists instead of only once the folder next
        // changes to a concrete one.
        this.calendarAppStateService.setSelectedTab(this.selectedTab);
      });
  }

  ngOnDestroy() {
    this.selectionChain.destroy();
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Actions
   */

  protected handleSelectedTabChange(tab: string): void {
    if (!isKnownTab<Exclude<ListState, 'tabs'>>(tab, this.tabs)) {
      return;
    }
    this.selectedTab = tab;
    this.calendarAppStateService.setSelectedTab(tab);
  }

  handleScrollOptionChange(value: number) {
    this.listScrollPosition = null;
    setTimeout(() => (this.listScrollPosition = value));
  }

  handleNewEvent(event = new CalendarEvent()) {
    this.calendarAppStateService.setMainView(event);
  }

  handleNewCalendar(calendar = new Calendar()) {
    this.calendarAppStateService.setMainView(calendar);
  }

  protected handleSearch(title: string) {
    this.searchTitle.next(title);
  }

  protected handleDoubleClickEvent(summary: CalendarEventSummaryDto) {
    this.eventsService
      .getItem(summary.id)
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((event) => {
        this.modalService.openFullForm(event, this.injector);
      });
  }

  protected handleDoubleClickCalendar(summary: CalendarSummaryDto) {
    this.calendarsService
      .getItem(summary.id)
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((calendar) => {
        this.modalService.openFullForm(calendar, this.injector);
      });
  }

  protected handleClickEvent(summary: CalendarEventSummaryDto) {
    this.eventsService
      .getItem(summary.id)
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((event) => {
        this.calendarAppStateService.setMainView(event);
      });
  }

  protected handleClickMessage(summary: MessageSummaryDto) {
    this.messagesService
      .getItem(summary)
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((message) => {
        this.calendarAppStateService.setMainView(message);
      });
  }

  protected handleDoubleClickMessage(summary: MessageSummaryDto) {
    this.messagesService
      .getItem(summary)
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((message) => {
        this.modalService.openFullForm(message, this.injector);
      });
  }

  protected handleClickCalendar(summary: CalendarSummaryDto) {
    this.calendarsService
      .getItem(summary.id)
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((calendar) => {
        this.calendarAppStateService.setMainView(calendar);
      });
  }

  doDebug() {
    switch (this.debug) {
      case 'score':
        this.debug = null;
        break;
      default:
        this.debug = 'score';
    }
  }
}
